import { useEffect, useMemo, useRef, useState } from "react"

import { Timeframe, Timeframes } from "wealthmode-frontend"
import { ActionButton, Graph } from "wealthmode-frontend/components"
import { Box, HStack, Link, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoney, formatMoneyFull } from "wealthmode-frontend/util"

import LogoDark from "~/assets/logo-name-dark.svg"

import { Config } from "@/Config"
import { GraphHitOverlay } from "@/components/GraphHitOverlay"
import { GraphValues } from "@/components/GraphValues"
import { SliderFormField, formatPercentLabel } from "@/components/SliderFormField"
import { trajectory, trajectoryValueAt } from "@/lib/trajectory"

const PORTFOLIO_MIN = 100_000
const PORTFOLIO_MAX = 10_000_000
const PORTFOLIO_STEP = 100_000
const AUM_FEE_MIN = 0
const AUM_FEE_MAX = 2
const AUM_FEE_STEP = 0.1
const CAGR_MIN = 1
const CAGR_MAX = 15
const CAGR_STEP = 0.5
const CAGR_DEFAULT = 13

const marketCAGRs = {
  3: 0.21,
  5: 0.12,
  10: 0.1501,
} as Record<number, number>
const futureCAGR = 0.1054
const WEALTHMODE_GROWTH_RATE = futureCAGR
const GRAPH_HEADLINE_SCALE = 2
const GRAPH_VISUAL_HEIGHT = 700

const GRAPH_WINNING_SERIES_OPTIONS = {
  fillBelow: {
    extendTo: "axis" as const,
    colors: ["green-500", "green-500", "transparent"],
  },
  glow: {
    color: "green-500",
    distance: 40,
    width: 8,
  },
}

export default function Index() {
  const headlineRef = useRef<React.ComponentRef<typeof VStack>>(null)
  const [graphWidth, setGraphWidth] = useState<number | null>(null)

  const [portfolioAmount, setPortfolioAmount] = useState(500_000)
  const [aumFee, setAumFee] = useState(1)
  const [cagr, setCAGR] = useState(CAGR_DEFAULT)

  const portfolioValue = useMemo(() => Math.max(1, portfolioAmount), [portfolioAmount])
  const aumFeeRate = useMemo(() => aumFee / 100, [aumFee])
  const pomgr = useMemo(() => (cagr / 100 / marketCAGRs[10]) * futureCAGR, [cagr, marketCAGRs, futureCAGR])
  const advisorSeriesData = useMemo(
    () => trajectory(portfolioValue, pomgr, aumFeeRate),
    [portfolioValue, futureCAGR, aumFeeRate, cagr],
  )
  const wealthmodeSeriesData = useMemo(() => trajectory(portfolioValue, WEALTHMODE_GROWTH_RATE, 0), [portfolioValue])

  const [timeframe, setTimeframe] = useState<Timeframe>(Timeframes.Y30)

  useEffect(() => {
    const el = headlineRef.current as unknown as Element | null
    if (el == null || typeof ResizeObserver === "undefined") {
      return
    }

    const updateGraphWidth = () => {
      const { width } = el.getBoundingClientRect()
      if (width <= 0) {
        return
      }

      const nextWidth = width * GRAPH_HEADLINE_SCALE - 100
      setGraphWidth((prev) => (prev === nextWidth ? prev : nextWidth))
    }

    const observer = new ResizeObserver(updateGraphWidth)
    observer.observe(el)
    updateGraphWidth()

    return () => observer.disconnect()
  }, [])

  const currentTrajectory = trajectoryValueAt(advisorSeriesData, timeframe.years)
  const improvedTrajectory = trajectoryValueAt(wealthmodeSeriesData, timeframe.years)

  const graphContainerStyle = useMemo(
    () => ({
      height: GRAPH_VISUAL_HEIGHT,
      width: graphWidth ?? 720,
    }),
    [graphWidth],
  )

  const graphSeries = useMemo(
    () => [
      {
        data: advisorSeriesData,
        color: currentTrajectory > improvedTrajectory ? "green-500" : "foreground-primary",
        ...(currentTrajectory > improvedTrajectory ? GRAPH_WINNING_SERIES_OPTIONS : {}),
      },
      {
        data: wealthmodeSeriesData,
        color: improvedTrajectory > currentTrajectory ? "green-500" : "foreground-primary",
        ...(improvedTrajectory > currentTrajectory ? GRAPH_WINNING_SERIES_OPTIONS : {}),
      },
    ],
    [advisorSeriesData, wealthmodeSeriesData, currentTrajectory, improvedTrajectory],
  )

  const graphXAxis = useMemo(
    () => [
      { value: 0, label: "", selectable: false },
      { value: 10, label: "10Y" },
      { value: 20, label: "20Y" },
      { value: 30, label: "30Y" },
    ],
    [],
  )

  const graphXDomain = useMemo(() => {
    const xValues = graphSeries.flatMap((series) => series.data.map((point) => point.x))
    return { xMin: Math.min(...xValues), xMax: Math.max(...xValues) }
  }, [graphSeries])

  // Shared between the desktop slot (above the values) and the mobile slot (below the
  // graph). Rendered in two positions so the graph can sit above its controls on mobile
  // without disturbing the graph's background scaffolding.
  const sliders = (
    <>
      <HStack className="w-full shrink-0">
        <SliderFormField
          label="Portfolio amount"
          displayValue={formatMoneyFull(portfolioAmount) ?? ""}
          value={portfolioAmount}
          onChange={setPortfolioAmount}
          minValue={PORTFOLIO_MIN}
          maxValue={PORTFOLIO_MAX}
          step={PORTFOLIO_STEP}
          minLabel={formatMoney(PORTFOLIO_MIN) ?? ""}
          maxLabel={formatMoney(PORTFOLIO_MAX) ?? ""}
          accent="blue"
          className="flex-1"
        />
      </HStack>
      <HStack className="w-full shrink-0 gap-16 max-[500px]:gap-10">
        <SliderFormField
          label="AUM Fee"
          displayValue={formatPercentLabel(aumFee)}
          value={aumFee}
          onChange={setAumFee}
          minValue={AUM_FEE_MIN}
          maxValue={AUM_FEE_MAX}
          step={AUM_FEE_STEP}
          minLabel={formatPercentLabel(AUM_FEE_MIN)}
          maxLabel="2%"
          accent="purple"
          className="flex-1"
        />
        <SliderFormField
          label="Annual Return"
          displayValue={formatPercentLabel(cagr)}
          value={cagr}
          onChange={setCAGR}
          minValue={CAGR_MIN}
          maxValue={CAGR_MAX}
          step={CAGR_STEP}
          minLabel={formatPercentLabel(CAGR_MIN)}
          maxLabel={formatPercentLabel(CAGR_MAX)}
          accent="yellow"
          className="flex-1"
        />
      </HStack>
    </>
  )

  return (
    <VStack className="relative w-full min-w-175 items-center overflow-x-hidden mb-40">
      <VStack className="w-full max-w-300 items-center px-6 py-12 pointer-events-auto relative z-10">
        <Box
          className="pointer-events-auto absolute inset-x-0 bottom-0"
          style={{ height: GRAPH_VISUAL_HEIGHT }}
        />

        <VStack className="relative w-full items-center gap-16 px-4">
          <LogoDark className="h-12 shrink-0 mb-20" />

          <VStack key="headline">
            <Text className="text-2xl text-foreground-primary text-center">Financial advisors cost a LOT.</Text>
            <Text className="text-md sm:text-xl text-foreground-secondary text-center">
              See how their fees impact your net worth.
            </Text>
          </VStack>

          <VStack
            key="content"
            className="w-full shrink-0 max-w-300 self-center max-sm:hidden"
          >
            {sliders}
          </VStack>

          <VStack
            ref={headlineRef}
            className="min-w-175 max-w-300 w-full shrink-0 self-center"
          >
            <HStack className="w-full items-center justify-around">
              <GraphValues
                key="current"
                title={"Advisor"}
                value={currentTrajectory}
                valueType={"money"}

                alignment="center"
                textClassName={
                  currentTrajectory > improvedTrajectory ? "text-information-positive" : "text-foreground-primary/80"
                }
                legend={currentTrajectory > improvedTrajectory ? "information-positive" : "foreground-primary"}
              />

              <GraphValues
                key="improved"
                title={"Wealthmode"}
                titleClassName="text-foreground-secondary"
                value={improvedTrajectory}
                valueType={"money"}
                alignment="center"
                textClassName={
                  improvedTrajectory > currentTrajectory ? "text-information-positive" : "text-foreground-primary/80"
                }
                legend={improvedTrajectory > currentTrajectory ? "information-positive" : "foreground-primary"}
              />
            </HStack>
          </VStack>
        </VStack>
      </VStack>

      {/* Anchor: fixed layout height; graph paints upward/sideways beyond this box. */}
      <Box className="pointer-events-none relative h-120 w-full overflow-visible">
        <Box
          className="graph-edge-mask pointer-events-none absolute bottom-0 left-1/2 z-0 -translate-x-1/2 overflow-visible"
          style={graphContainerStyle}
        >
          <Graph
            className="h-full w-full pb-10 overflow-x-hidden overflow-y-visible"
            aspectRatio={null}
            series={graphSeries}
            xAxis={graphXAxis}
            cursorValue={timeframe.years}
            cursorColors={{
              line: "foreground-secondary",
              gripper: "foreground-secondary",
              dots: "foreground-contrast",
            }}
            zeroAxisGap={30}
            hideZeroLine={true}
            xAxisLabelPosition="above"
            xAxisTicks
          />
        </Box>
        <Box
          className="pointer-events-auto absolute bottom-0 left-1/2 z-10 -translate-x-1/2"
          style={graphContainerStyle}
        >
          <GraphHitOverlay
            className="h-full w-full"
            xAxis={graphXAxis}
            xMin={graphXDomain.xMin}
            xMax={graphXDomain.xMax}
            onCursorChange={(value) => setTimeframe(Timeframe.findByYears(value) ?? Timeframes.Y20)}
          />
        </Box>
      </Box>

      <VStack className="hidden max-sm:flex w-full max-w-300 shrink-0 self-center px-10 mt-8">{sliders}</VStack>

      <VStack className="w-full max-w-300 items-center px-6 py-12 gap-24 mt-20">
        <VStack className="gap-4">
          <Box>
            <Text className="text-2xl text-foreground-primary text-center">
              Most financial advisors underperform.<Text className="text-xl text-blue-500">*</Text>
            </Text>
            <Text className="text-md sm:text-xl text-foreground-secondary text-center">
              The US market returned about {(marketCAGRs[10] * 100).toFixed(0)}%/year over the last decade.
            </Text>
            <Text className="text-md sm:text-xl text-foreground-secondary text-center">
              Most advisors don't beat the market.
            </Text>
          </Box>
          <Link
            href="https://www.spglobal.com/spdji/en/spiva/article/spiva-global/"
            isExternal
          >
            <Text className="text-sm text-foreground-tertiary text-center underline">
              <Text className="text-sm text-blue-500">*</Text>S&P Global
            </Text>
          </Link>
        </VStack>

        <ActionButton
          variant="primary"
          text="Get started with personalized advice"
          className="w-175"
          onPress={openApp}
        />
      </VStack>
    </VStack>
  )
}

// Any referral code is already in a domain-scoped cookie, so it need not ride on this URL.
function openApp() {
  const appConfig = Config.get("app") as { web?: { base_url?: string } } | undefined
  const baseURL = appConfig?.web?.base_url

  if (!baseURL) {
    console.error("App base_url missing from config; cannot open the app")
    return
  }

  window.location.href = baseURL
}
